import { useMemo, useState } from "react";

import { motion } from "framer-motion";
import { useDispatch } from "react-redux";
import {
  Building2,
  CheckCircle2,
  LandPlot,
  MapPin,
} from "lucide-react";

import { postAPIPayload } from "../../../apis/postApi";
import { formatTitleOwnerNamesFromJson } from "../../helpers/utils";
import { Button } from "../../common/customDesignedComponents/button";
import { Checkbox } from "../../common/customDesignedComponents/checkbox";
import { propertiesAction } from "../../../redux/slices/properties/propertiesSlice";

export type PropertyCandidate = {
  property_id: string;
  display_address: string;
  parcel_number?: string | null;
  owner_name?: string | null;
  county?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  is_raw_land?: boolean;
  address_matched?: boolean;
  json_response: Record<string, unknown>;
  address?: string;
  street_number?: string | null;
  site_route?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  country?: string;
};

export type MailingMatchesResponse = {
  eligible?: boolean;
  reason?: string | null;
  session_id?: string | null;
  id_mailing_address?: {
    address_line?: string;
    city?: string;
    state?: string;
    zip?: string;
  } | null;
  counties?: string[];
  candidates?: PropertyCandidate[];
};

type AddPropertyMailingMatchesProps = {
  userId: number;
  matches: MailingMatchesResponse;
  onSearchByAddress: (showNoMatchesBanner: boolean) => void;
};

const AddPropertyMailingMatches = ({
  userId,
  matches,
  onSearchByAddress,
}: AddPropertyMailingMatchesProps) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const candidates = useMemo(
    () => matches?.candidates || [],
    [matches?.candidates],
  );

  const [selectedIds, setSelectedIds] = useState<string[]>(() =>
    candidates.length === 1 ? [String(candidates[0].property_id)] : [],
  );

  const toggleProperty = (propertyId: string, checked: boolean) => {
    setSelectedIds((current) => {
      if (checked) {
        return current.includes(propertyId) ? current : [...current, propertyId];
      }
      return current.filter((id) => id !== propertyId);
    });
  };

  const handleContinue = async () => {
    if (!matches?.session_id || selectedIds.length === 0) {
      setError("Select at least one property to add.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const selectedProperties = candidates.filter((property) =>
        selectedIds.includes(property.property_id),
      );

      const response = await postAPIPayload({
        url: `properties/ep-aware-verification/${matches.session_id}/apply-properties`,
        data: {
          user_id: userId,
          properties: selectedProperties,
          step: 2,
          step_key: "VERIFY_VIA_EPAWARE",
          require_mailing_address_match: true,
        },
        token: true,
      });

      if (!response?.success) {
        throw new Error(response?.message || "Unable to add the selected properties.");
      }

      const primaryProperty = selectedProperties[0];
      dispatch(propertiesAction.selectedUserPropertyData(primaryProperty));
      dispatch(
        propertiesAction.setGeneratedQuestionsData({
          success: true,
          message:
            response?.message || "Property verified with your government ID.",
          data: {
            is_verified: true,
            owner_verified: true,
            property_id: primaryProperty.property_id,
            ep_aware_session_id: matches.session_id,
            ...(response?.data || {}),
          },
        }),
      );
    } catch (submitError: any) {
      setError(submitError?.message || "Unable to add the selected properties.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderPropertyCard = (property: PropertyCandidate) => {
    const checked = selectedIds.includes(property.property_id);
    const ownerDisplayName = formatTitleOwnerNamesFromJson(property);

    return (
      <div
        key={property.property_id}
        role="button"
        tabIndex={0}
        onClick={() => toggleProperty(property.property_id, !checked)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleProperty(property.property_id, !checked);
          }
        }}
        className={`flex items-start gap-4 rounded-2xl border p-4 cursor-pointer transition-all ${
          checked
            ? "border-secondary bg-secondary/5 shadow-sm"
            : "border-border hover:border-secondary/50"
        }`}
      >
        <Checkbox
          checked={checked}
          onCheckedChange={(value) =>
            toggleProperty(property.property_id, value === true)
          }
          onClick={(event) => event.stopPropagation()}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            {property.is_raw_land ? (
              <LandPlot className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
            ) : (
              <Building2 className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
            )}
            <div>
              <p className="font-medium text-foreground">{property.display_address}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {property.is_raw_land ? "Raw land / parcel" : "Improved property"}
                {property.parcel_number ? ` • Parcel ${property.parcel_number}` : ""}
                {property.county ? ` • ${property.county}` : ""}
              </p>
              {ownerDisplayName ? (
                <p className="text-xs text-muted-foreground mt-1">
                  Owner: {ownerDisplayName}
                </p>
              ) : null}
              {property.address_matched ? (
                <p className="text-xs text-emerald-700 mt-2 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Mailing address matches your ID
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!matches?.eligible || candidates.length === 0) {
    return null;
  }

  const countyLabel =
    (matches.counties || []).length === 1
      ? matches.counties?.[0]
      : (matches.counties || []).join(", ");

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-secondary/10 text-secondary mb-4">
          <MapPin className="w-7 h-7" />
        </div>
        <h2 className="text-xl font-display font-medium text-foreground mb-2">
          Properties matching your mailing address
        </h2>
        <p className="text-sm text-muted-foreground">
          Your government ID is already on file. Select any property below to add
          it without verifying again
          {countyLabel ? ` in ${countyLabel}` : ""}.
        </p>
        {matches.id_mailing_address?.address_line ? (
          <p className="text-xs text-muted-foreground mt-2">
            ID mailing address: {matches.id_mailing_address.address_line}
            {matches.id_mailing_address.city
              ? `, ${matches.id_mailing_address.city}`
              : ""}
            {matches.id_mailing_address.state
              ? `, ${matches.id_mailing_address.state}`
              : ""}
            {matches.id_mailing_address.zip
              ? ` ${matches.id_mailing_address.zip}`
              : ""}
          </p>
        ) : null}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-3"
      >
        {candidates.map((property) => renderPropertyCard(property))}
      </motion.div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          type="button"
          variant="outline"
          onClick={() => onSearchByAddress(true)}
        >
          Search a different address
        </Button>
        <Button
          type="button"
          onClick={handleContinue}
          disabled={submitting || selectedIds.length === 0}
        >
          {submitting
            ? "Adding selected properties..."
            : `Continue to billing (${selectedIds.length})`}
        </Button>
      </div>
    </div>
  );
};

export default AddPropertyMailingMatches;
